<template>
  <div class="group-field-table">
    <div class="table-header" v-if="index === 0">
      <div v-for="field in fields" :key="field.key" class="table-cell">
        {{ field.label }}
      </div>
    </div>
    <div class="table-body">
      <div v-for="(field, index) in fields" :key="index" class="table-cell">
        <template v-if="field.type === 'reference'">
          {{ getReferenceValue(field, value[field.key]) }}
        </template>
        <template v-else-if="field.type === 'list'">
          {{ getListValue(field, value[field.key]) }}
        </template>
        <template v-else-if="field.type === 'date'">
          {{ formatDate(value[field.key]) }}
        </template>
        <template v-else-if="field.type === 'datetime'">
          {{ formatDateTime(value[field.key]) }}
        </template>
        <template v-else>
          {{ value[field.key] }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "ViewGroupFieldTable",
  props: {
    fields: {
      type: Array,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    lists: {
      type: Array,
      default: () => [],
    },
    models: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      referenceOptions: {},
    };
  },
  created() {},
  mounted() {
    this.loadReferenceOptions();
  },
  beforeMount() {},
  methods: {
    async loadReferenceOptions() {
      for (const field of this.fields) {
        if (field.type === "reference" && field.ref) {
          try {
            const { data } = await wencesApi.get(
              `/api/gotrading/dynamic/${field.ref}`
            );
            this.$set(this.referenceOptions, field.key, data);
          } catch (error) {
            console.error(
              `Error loading reference options for ${field.key}:`,
              error
            );
            this.$set(this.referenceOptions, field.key, []);
          }
        }
      }
    },
    getReferenceValue(field, value) {
      if (!value) return "";

      const refOptions = this.referenceOptions[field.key];
      if (!refOptions) return value;

      // Si el valor es un objeto con _id
      if (typeof value === "object" && value._id) {
        const refItem = refOptions.find((item) => item._id === value._id);
        return refItem
          ? refItem.name || refItem.label || refItem._id
          : value._id;
      }

      // Si el valor es un string (el _id)
      if (typeof value === "string") {
        const refItem = refOptions.find((item) => item._id === value);
        return refItem ? refItem.name || refItem.label || refItem._id : value;
      }

      return value;
    },
    getListValue(field, value) {
      if (!value) return "";

      // Si es una lista múltiple
      if (field.isArray && Array.isArray(value)) {
        return value.map((v) => this.getListOptionLabel(field, v)).join(", ");
      }

      // Si es una lista simple
      return this.getListOptionLabel(field, value);
    },
    getListOptionLabel(field, value) {
      if (!value) return "";

      // Si el valor es un objeto con _id, usar el _id
      const id = typeof value === "object" && value._id ? value._id : value;

      // Buscar en los listados disponibles
      const list = this.lists?.find((l) => l.type === field.listType);
      if (!list || !list.values) return id;

      // Buscar el valor en la lista
      const option = list.values.find((opt) => opt._id === id);
      return option ? option.label : id;
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString();
    },
    formatDateTime(value) {
      if (!value) return "";
      return new Date(value).toLocaleString();
    },
  },
};
</script>

<style scoped>
/* Bootstrap grid classes */
.col-md-2 {
  flex: 0 0 16.666667%;
  max-width: 16.666667%;
}

.col-md-4 {
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
}

.col-md-6 {
  flex: 0 0 50%;
  max-width: 50%;
}

.col-md-8 {
  flex: 0 0 66.666667%;
  max-width: 66.666667%;
}

.col-md-10 {
  flex: 0 0 83.333333%;
  max-width: 83.333333%;
}

.col-md-12 {
  flex: 0 0 100%;
  max-width: 100%;
}

/* Utility classes */
.border {
  border: 1px solid #dee2e6 !important;
}

.p-2 {
  padding: 0.5rem !important;
}

/* Table styles */
.group-field-table {
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.table-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}

.table-cell {
  padding: 0.75rem;
  border-right: 1px solid #dee2e6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-cell:last-child {
  border-right: none;
}

.table-header .table-cell {
  font-weight: 600;
  color: #495057;
}

/* Responsive behavior */
@media (min-width: 768px) {
  .col-md-2,
  .col-md-4,
  .col-md-6,
  .col-md-8,
  .col-md-10,
  .col-md-12 {
    position: relative;
    width: 100%;
    padding-right: 15px;
    padding-left: 15px;
  }
}
</style>
